function invalid(code = 'PROCUREMENT_SUMMARY_INVALID') {
  throw Object.assign(new Error('Currency totals are unavailable.'), { code });
}
const count = value => Number.isSafeInteger(value) && value >= 0;
const decimal = value => typeof value === 'string' && /^-?[0-9]{1,80}(?:\.[0-9]{1,20})?$/.test(value);

// Keep PostgreSQL decimal strings exact, including amounts above Number's safe range.
export function formatDashboardMoney(value, currency, locale) {
  if (!decimal(value) || !/^[A-Z]{3}$/.test(currency || '') || typeof locale !== 'string') invalid();
  const format = new Intl.NumberFormat(locale, { style: 'currency', currency, currencyDisplay: 'code' });
  const digits = format.resolvedOptions().maximumFractionDigits;
  const [whole, fraction = ''] = value.replace(/^-/, '').split('.');
  const factor = 10n ** BigInt(digits);
  let minor = BigInt(whole) * factor + BigInt(fraction.padEnd(digits, '0').slice(0, digits) || '0');
  if (fraction.length > digits && fraction[digits] >= '5') minor++;
  const negative = value.startsWith('-') && minor !== 0n;
  const integer = minor / factor;
  const signed = integer === 0n ? (negative ? -0 : 0) : (negative ? -integer : integer);
  return format.formatToParts(signed).map(part => part.type === 'fraction'
    ? (minor % factor).toString().padStart(digits, '0') : part.value).join('');
}

export function validateDashboardSummary(value, user) {
  if (value?.version !== 2 || value.currency_basis !== 'stored_unit_gross_total') invalid();
  for (const [key, expected] of [['company_id', user?.company_id], ['unit_id', user?.unit_id], ['user_id', user?.id]]) {
    if (!/^[1-9][0-9]*$/.test(String(expected ?? '')) || String(value.scope?.[key]) !== String(expected)) {
      invalid('PROCUREMENT_SUMMARY_CONTEXT_CHANGED');
    }
  }
  if (!/^[A-Z]{3}$/.test(value.currency || '') || typeof value.locale !== 'string' ||
      typeof value.timezone !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value.as_of_date || '') ||
      !count(value.checked_pos) || !Number.isFinite(Date.parse(value.updated_at))) invalid();
  try {
    if (new Date(value.as_of_date + 'T00:00:00Z').toISOString().slice(0, 10) !== value.as_of_date) invalid();
    new Intl.DateTimeFormat(value.locale, { timeZone: value.timezone });
    formatDashboardMoney('0', value.currency, value.locale);
  } catch { invalid(); }
  for (const key of ['received_pos', 'partial_pos', 'unapproved_pos', 'arriving_today_count']) {
    if (!count(value.totals?.[key]) || value.totals[key] > value.checked_pos) invalid();
  }
  for (const [key, moneyKey] of [['arriving_today', 'arriving_today_cost'], ['raised_week_pending', 'raised_week_pending_cost']]) {
    const coverage = value.coverage?.[key];
    if (!coverage || !['po_count', 'valued_po_count', 'missing_po_count'].every(k => count(coverage[k])) ||
        coverage.po_count > value.checked_pos || coverage.valued_po_count + coverage.missing_po_count !== coverage.po_count ||
        coverage.status !== (coverage.missing_po_count ? 'incomplete' : 'complete')) invalid();
    if (coverage.missing_po_count ? value.totals[moneyKey] !== null : !decimal(value.totals[moneyKey])) invalid();
  }
  if (value.coverage.arriving_today.po_count !== value.totals.arriving_today_count) invalid();
  return value;
}

export function dashboardMoneyMetric(summary, key) {
  if (!summary) return { value: 'Unavailable', hint: 'Currency totals unavailable. Refresh to try again.' };
  const moneyKey = { arriving_today: 'arriving_today_cost', raised_week_pending: 'raised_week_pending_cost' }[key];
  if (!moneyKey) invalid();
  const coverage = summary.coverage[key];
  if (coverage.missing_po_count) return { value: 'Unavailable',
    hint: `${coverage.missing_po_count} of ${coverage.po_count} POs lack a usable ${summary.currency} total.` };
  return { value: formatDashboardMoney(summary.totals[moneyKey], summary.currency, summary.locale),
    hint: `Gross value in ${summary.currency} · ${summary.as_of_date} (${summary.timezone})` };
}
